import { Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import Toast from 'components/dumb/Toast';
import VerifyOtp from 'components/smart/VerifyOtp';
import UnberryModal from 'components/dumb/UnberryModal';
import UserForm from 'components/smart/UserForm';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import * as AuthSuppliers from 'store/auth/actions';
import { AUTH_CONSTANTS } from '../../constants/authConstants';
import GamePlayApis from '../../services/apis/gameplay';
import AuthApi from '../../services/apis/auth';
import * as LoadingScreenUtils from './screenUtils';
import ProctoringApi from 'services/apis/proctoring';
import { storage } from '../../services/config/storage';
import * as Sentry from '@sentry/react';
import { LanguageSelect } from 'components/common/LanguageSelect';
import { saveLanguage } from 'store/translation/actions';
// styles
import './style.scss';
import { initializeChatbot, resolveChatbotType } from 'services/chatbot';
import { useIsMobileDevice } from 'hooks';
import analytics from 'services/config/analytics';

import HeroPageTemplate from 'components/dumb/HeroPageTemplate';
import PhoneInput from 'components/smart/PhoneInput';
import PermissionsModal from 'components/dumb/PermissionsModal';
import { chatbotTypes, COUNTRY_DATA, DEFAULT_COUNTRY_DATA, GODREJ_SECTION_IDS, LOGIN_TYPE, ORG_SET_EVENT, PERMISSION_DEVICE_TYPES, PERMISSION_ERRORS, POST_WORKSHOP_GODREJ, PRE_WORKSHOP_GODREJ, PROCTORING_TYPES } from 'constants/globalConstants';
import { validators } from 'views/SignUp/validate';
import { useGameplayConfig } from 'store';
import { Cross, ErrorWarning } from 'assets/icons';
import { convertBlobToBase64, loadScript } from 'utils/helper';
import useLoginType from 'views/hooks/useLoginType';
import { initializeFreshChat } from 'services/freshchat';

const LoadingScreen = (props) => {
  // ===================
  // props from parent
  // ===================
  // props from redux
  // ===================
  const error = useSelector(({ auth }) => auth.error);
  const dispatch = useDispatch();

  // ===================
  // custom hooks
  // ===================
  const isMobile = useIsMobileDevice();
  const [userDetailForm] = Form.useForm();
  const [loginForm] = Form.useForm();
  const history = useHistory();
  const location = useLocation();
  let { id: linkId } = useParams();
  const {
    positionId,
    isLoading,
    isVideoProctored,
    customizableForm,
    setIsVideoProctored,
    setIsProctored,
    fetchLinkConfig,
    isProctored,
    languagesOptions,
    fetchConfig,
    isScreenProctored,
    isAudioProctored,
    configLanguageSettings,
    candidateInfo,
  } = useGameplayConfig()
  const { t } = useTranslation()

  // ===================
  // constant variables  
  // ===================
  const loginType = useLoginType(isLoading, positionId);
  const hasPhoneLogin = loginType === LOGIN_TYPE.PHONE;
  const hasThirdPartyLogin = loginType === LOGIN_TYPE.THIRD_PARTY;

  const hasGodrejPosition = GODREJ_SECTION_IDS.includes(positionId);
  const isPostWorkshopPosition = POST_WORKSHOP_GODREJ.includes(positionId)
  const isPreWorkshopPosition = PRE_WORKSHOP_GODREJ.includes(positionId)

  // Re-using component for login through positionId/linkId
  const hasPositionLogin = location.pathname.includes("/login");
  const companyLogo = storage.get.companiesLogos();
  const orgInfo = storage.get.orgInfo()

  // Identify the candidate for GA4 for per-candidate reporting.
  //
  // We set the GA4 User-ID to the linkId (unique per candidate assessment) and
  // attach candidate_id / org / position as user properties so a single
  // candidate's full event timeline is searchable in GA4's User Explorer.
  //
  // IMPORTANT: GA4's Terms of Service forbid sending PII (email / name) to
  // Google. We only send opaque IDs. To map an id back to a real person, cross-
  // reference your own backend using the candidate_id / link_id.
  useEffect(() => {
    if (linkId) {
      analytics.identify(linkId, {
        candidate_id: candidateInfo?._id ?? candidateInfo?.candidateId ?? linkId,
        link_id: linkId,
        org_id: orgInfo?.orgId ?? orgInfo?._id,
        position_id: positionId,
      });
    }
  }, [linkId, positionId, candidateInfo]);

  const USER_LOGIN_INITIAL_VALUES = {
    candidateEmail: '',
    ...((hasPhoneLogin) && { phone: '' }),
    ...(hasThirdPartyLogin && { candidateKey: '' })
  };

  const USER_PROFILE_INITIAL_VALUES = {
    designation: '',
    dob: '',
    name: '',
  };
  // ===================
  // useStates
  // ===================
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpLoginSuccess, setOtpLoginSuccess] = useState(true);
  const [userData, setUserData] = useState(null);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [apiInProgress, setApiInProgress] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [maskUrlComp, setMaskUrlComp] = useState(true);
  const [candidateKey, setCandidateKey] = useState('');
  const [candidatePhone, setCandidatePhone] = useState(null);
  const [country, setCountry] = useState(DEFAULT_COUNTRY_DATA);
  const [submitting, setSubmitting] = useState(false)
  const [isPermissionModalVisible, setIsPermissionModalVisible] = useState(false)

  // ===================
  // useRefs
  // ===================

  // ===================
  // computed variables
  // ===================

  // ===================
  // functions
  // ===================

  /**
   * @return {isVideoSessionRequired} - true if gameplayconfig or linkconfig returns saveProctoredVideo as true
   */
  const updateGameplayConfig = async (candidateLinkId) => {
    let linkConfigResponse
    try {
      linkConfigResponse = await fetchLinkConfig(candidateLinkId)
      if (linkConfigResponse.success) {
        if (linkConfigResponse.linkPositionConfig) {
          setIsVideoProctored(!!linkConfigResponse?.linkPositionConfig?.saveProctoredVideo)
          setIsProctored(!!linkConfigResponse?.linkPositionConfig?.proctoring)
        }
      }
    } catch (err) {
      console.err(err)
    } finally {
      return {
        isVideoProctored: isVideoProctored || !!linkConfigResponse?.linkPositionConfig?.saveProctoredVideo,
        isProctored: isProctored || !!linkConfigResponse?.linkPositionConfig?.isProctored,
        isScreenProctored,
        isAudioProctored,
      }
    }
  }

  const createProctoring = async (candidateLinkId, type) => {
    const { proctoring: { id = "" } } = await ProctoringApi.createProctoring(candidateLinkId, { type })
    return id
  }

  const createVideoSession = async (proctoringId, type) => {
    const { sessionId = "" } = await ProctoringApi.createVideoSessionId(proctoringId)
    if (type === PROCTORING_TYPES.VIDEO) {
      storage.set.userVideoOrderCount(0)
      storage.set.userVideoSessionId(sessionId)
    } else if (type === PROCTORING_TYPES.SCREEN) {
      storage.set.screenVideoOrderCount(0)
      storage.set.screenVideoSessionId(sessionId)
    } else if (type === PROCTORING_TYPES.AUDIO) {
      storage.set.userAudioOrderCount(0)
      storage.set.userAudioSessionId(sessionId)
    }
  }

  /**
   *
   * @param {string} candidateEmail
   */
  const onCandidateKeySubmit = async ({ candidateKey, candidateEmail, phone, countryCode, posId = '' }) => {
    setSubmitting(true)
    setCandidateKey(candidateKey);
    setCandidatePhone(phone);
    const getPositionId = posId || positionId;
    const data = {
      candidateKey,
      email: candidateEmail,
      positionId: getPositionId,
    };
    try {
      const result = await dispatch(AuthSuppliers.verifyThirdPartyLogin(data));
      if (result) {
        const { user } = result;
        setSentryContext(user);
        const chatbot = resolveChatbotType(orgInfo?.organizationConfig?.supportChatbotType)
        if (chatbot === chatbotTypes.FRESHCHAT) {
          initializeFreshChat(user, isMobile);
        } else if (chatbot === chatbotTypes.UNBERRY) {
          initializeChatbot(user, isMobile);
        }
        // show user info form if name is not present for user
        redirectToGames(false, result?.linkId);
      } else {
        console.log(props);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting(false)
    }
  };

  /**
   *
   * @param {string} candidateEmail
   */
  const onEmailSubmit = async ({ candidateEmail, phone, countryCode, posId = '' }) => {
    setSubmitting(true)
    setCandidateEmail(candidateEmail);
    setCandidatePhone(phone);
    const getPositionId = posId || positionId;
    const data = {
      route: AUTH_CONSTANTS.LOGIN_ROUTE_ORIGIN,
      positionId: getPositionId,
      ...(!hasPhoneLogin && { email: candidateEmail }),
      ...(hasPhoneLogin && { phone }),
      ...(hasPhoneLogin && { countryCode: countryCode || country?.phone }),
    };
    analytics.track('otp_requested', { login_type: hasPhoneLogin ? 'phone' : 'email' });
    try {
      const result = await dispatch(AuthSuppliers.sendOtpEmail(data));
      if (result) setShowOTPModal(true)
    } catch (err) {
      analytics.track('login_failed', { stage: 'send_otp', error: err?.message });
      console.log(err);
    } finally {
      setSubmitting(false)
    }
  };

  // userLinkId -> Used in case of postionLogin
  const redirectToGames = async (replace = false, userLinkId) => {
    try {
      setShowUserDetailsModal(false);
      const candidateLinkId = hasPositionLogin ? userLinkId : linkId;
      if (candidateLinkId) {
        const response = await dispatch(AuthSuppliers.syncGameData({ linkId: candidateLinkId }));
        if (response) {
          storage.set.orgId(response?.games?.position?.organizationId)
          storage.set.linkId(candidateLinkId)
          const { isVideoProctored, isProctored, isScreenProctored, isAudioProctored: audioOn } = await updateGameplayConfig(candidateLinkId)
          if (isVideoProctored) {
            const proctoringId = await createProctoring(candidateLinkId, PROCTORING_TYPES.VIDEO)
            await createVideoSession(proctoringId, PROCTORING_TYPES.VIDEO)
          }
          if (isScreenProctored) {
            const proctoringId = await createProctoring(candidateLinkId, PROCTORING_TYPES.SCREEN)
            await createVideoSession(proctoringId, PROCTORING_TYPES.SCREEN)
          }
          // Audio Proctoring v2: independent audio-type proctoring + session.
          if (audioOn) {
            const proctoringId = await createProctoring(candidateLinkId, PROCTORING_TYPES.AUDIO)
            await createVideoSession(proctoringId, PROCTORING_TYPES.AUDIO)
          }
          if (isProctored) {
            try {
              const response = await ProctoringApi.getUserReferenceImg(candidateLinkId)
              if (response.status !== 404) {
                const blob = new Blob([response])
                const refImgSrc = await convertBlobToBase64(blob)
                storage.set.userRefImg(refImgSrc)
              }
            } catch (err) {
              setIsPermissionModalVisible(PERMISSION_ERRORS.API_BLOCK)
              return
            }
          }
          await saveLanguage(candidateLinkId, configLanguageSettings.configLanguage)
          replace
            ? history.replace(`/${customizableForm ? "form" : "rules"}/${candidateLinkId}`)
            : history.push(`/${customizableForm ? "form" : "rules"}/${candidateLinkId}`);
        } else {
          setShowOTPModal(false);
        }
      } else {
        history.push(`/rules`);
      }
    } catch (error) {
      Toast('error', error.message);
    }
  };

  const setSentryContext = (user) => {
    try {
      Sentry.setUser({ email: user.email, username: user.name, id: user.id });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  /**
   * submit OTP
   * @param {string} otp
   */
  const onOTPSubmit = async (otp) => {
    const data = {
      otp: otp,
      route: AUTH_CONSTANTS.LOGIN_ROUTE_ORIGIN,
      ...(!hasPhoneLogin && { email: candidateEmail }),
      ...(hasPhoneLogin && { phone: candidatePhone }),
      ...(hasPhoneLogin && { countryCode: country.phone }),
      positionId
    };

    analytics.track('otp_submitted', { login_type: hasPhoneLogin ? 'phone' : 'email' });
    try {
      const result = await dispatch(AuthSuppliers.verifyOtp(data));
      if (result) {
        analytics.track('login_succeeded', { login_type: hasPhoneLogin ? 'phone' : 'email' });
        const { user } = result;
        setSentryContext(user);
        const showGameplayOnly = storage.get.showGameplayOnly();
        if (!showGameplayOnly) {
          const chatbot = resolveChatbotType(orgInfo?.organizationConfig?.supportChatbotType)
          if (chatbot === chatbotTypes.FRESHCHAT) {
            initializeFreshChat(user, isMobile);
          } else if (chatbot === chatbotTypes.UNBERRY) {
            initializeChatbot(user, isMobile);
          }
        }
        storage.set.allTabSwitchCount(result?.tabSwitched ?? 0)
        // show user info form if name is not present for user
        const { name, designation } = user;
        if (!name) {
          userDetailForm.setFieldsValue({ name, designation });
          setShowUserDetailsModal(true);
          setUserData(user);
        } else {
          // Redirect user to linkId received in result.
          if (hasPositionLogin) {
            await redirectToGames(false, result?.linkId);
          } else {
            await redirectToGames();
          }
        }
        setShowOTPModal(false);
      } else {
        analytics.track('otp_invalid');
        setOtpLoginSuccess(false);
        console.log(props);
      }
    } catch (err) {
      analytics.track('login_failed', { stage: 'verify_otp', error: err?.message });
      console.log(err);
    }
  };

  /**
   * updates user's profile with folowing values
   * @param {Object} values { name: '', designation: '', dob: '' }
   */
  const onUserDetailsSubmit = async (values) => {
    const payload = {
      ...values,
      // dob: moment(values.dob).format('YYYY-MM-DD'),
    };
    try {
      setApiInProgress(true);
      const response = await GamePlayApis.updateUserProfile(
        userData.id,
        payload
      );
      if (response) {
        setShowUserDetailsModal(false);
        setApiInProgress(false);
        redirectToGames();
      }
    } catch (err) {
      setApiInProgress(false);
      console.log('error', err);
    }
  };

  /**
   show error screen if login bypass fails
  */
  const MaskedUrlErrorComponent = () => (
    <>
      <p className="color-secondary sub-content opacity font-medium size-20 mt-30 mb-40">
        Some error occured. Open link from email again or follow below link for
        further instructions.
      </p>
      <p className="color-secondary sub-content opacity font-medium flex justify-center items-center size-20 mt-30 mb-40">
        <a target="_blank" href={storage.get.url() ? storage.get.url() : '#'}>
          Click Here
        </a>
      </p>
    </>
  );
  const bypassOtpAuth = async () => {
    try {
      const response = await AuthApi.getUserInfo();
      if (response.success) {
        const showGameplayOnly = storage.get.showGameplayOnly();
        const { user } = response;
        setSentryContext(user);
        if (!showGameplayOnly) {
          const chatbot = resolveChatbotType(orgInfo?.organizationConfig?.supportChatbotType)
          if (chatbot === "freshchat") {
            initializeFreshChat(user, isMobile);
          } else if (chatbot === "unberry-bot") {
            initializeChatbot(user, isMobile);
          }
        }
        storage.set.fcData(user);
        
        // Check if multiple languages are available and redirect to language selection
        if (languagesOptions && languagesOptions.length > 1) {
          history.replace(`/language-selection/${linkId}`);
        } else {
          redirectToGames(true);
        }
      }
    } catch (err) {
      setShowLoader(false);
      console.error('error', err);
    }
  };

  // ===================
  // useCallback
  // ===================

  // ===================
  // useEffects
  // ===================

  // To bypass login authentication if url contains auth code and callback url
  // config stat not needed as useGameplayConfig is not dependent on url change anymore. hence, this page is
  // not mounting twice and the otp call hits only once.
  useEffect(() => {
    if (location.state) {
      const {
        email: candidateEmail,
        country: candidateCountry,
        phone,
        candidateKey,
        posId,
        redirectedFromRandomizer,
      } = location.state;
      if (redirectedFromRandomizer) fetchConfig(false)
      if (candidateCountry) setCountry(candidateCountry);
      if (candidateKey) setCandidateKey(candidateKey);
      if (hasThirdPartyLogin) {
        loginForm.setFieldsValue({ candidateEmail, candidateKey });
        onCandidateKeySubmit({
          candidateKey,
          candidateEmail,
          posId,
        });
      } else {
        loginForm.setFieldsValue({ candidateEmail, phone });
        onEmailSubmit({
          candidateEmail,
          phone,
          posId,
          ...(candidateCountry && { countryCode: candidateCountry.phone })
        });
      }
    } else {
      const countryCode = orgInfo?.organizationConfig?.countryCode ?? "91"
      const country = COUNTRY_DATA?.find(country => country?.phone == countryCode)
      setCountry(country)
    }
  }, []);

  /**Dynamic country code from org config as per Neha. */

  useEffect(() => {
    const lsFcData = storage.get.fcData()
    storage.destroy.allExceptFeedbackSubmittedAndUserDisqualified(lsFcData?.id)
    storage.destroy.authToken()
    storage.destroy.showGameplayOnly()
    storage.destroy.feedbackAllowed()
    storage.destroy.userDisqualified()

    const queryParams = new URLSearchParams(location.search);
    const auth = queryParams.get('auth');
    const url = queryParams.get('url');
    const email = queryParams.get('email');
    const showGameplayOnly = JSON.parse(
      queryParams.get('showGameplayOnly') ?? 'false'
    );
    storage.set.linkId(linkId);

    if (auth && (url || showGameplayOnly)) {
      const feedbackAllowed = JSON.parse(queryParams.get('feedbackAllowed'));

      // set Params to local storage
      storage.set.authToken(auth);
      storage.set.url(url);
      storage.set.showGameplayOnly(showGameplayOnly);
      storage.set.feedbackAllowed(feedbackAllowed);

      // Remove params from url
      // queryParams.delete('url');
      // queryParams.delete('auth');
      // history.location.search = queryParams?.toString();
      // history.replace(history.location);

      // fetch User Data to bypass OTP
      bypassOtpAuth();
    } else if (auth && !url) {
      // showing error in case url is missing
      setShowLoader(false);
    } else if (email) {
      setShowLoader(false);
      setMaskUrlComp(false);

      loginForm.setFieldsValue({ candidateEmail: email });
    } else {
      // if auth & url missing show normal flow
      setShowLoader(false);
      setMaskUrlComp(false);
    }
  }, []);

  useEffect(() => {
    if (!candidateInfo || hasPositionLogin) return;
    if (loginType === LOGIN_TYPE.EMAIL && candidateInfo.email) {
      loginForm.setFieldsValue({ candidateEmail: candidateInfo.email });
    } else if (loginType === LOGIN_TYPE.PHONE && candidateInfo.phone) {
      const countryCode = candidateInfo.countryCode?.replace('+', '');
      const candidateCountry = COUNTRY_DATA.find(c => c.phone == countryCode);
      if (candidateCountry) setCountry(candidateCountry);
      loginForm.setFieldsValue({ phone: candidateInfo.phone });
      setCandidatePhone(candidateInfo.phone);
    }
  }, [candidateInfo, loginType]);

  // The configured login type can resolve after the form has mounted, and the OTP step is a
  // modal over this form. Re-apply the number the candidate typed so going back from the OTP
  // step leaves them on a phone sign-in that is still filled in.
  useEffect(() => {
    if (hasPhoneLogin && candidatePhone) {
      loginForm.setFieldsValue({ phone: candidatePhone });
    }
  }, [hasPhoneLogin, candidatePhone, showOTPModal]);

  useEffect(() => {
    if (!showOTPModal) setOtpLoginSuccess(true);
  }, [showOTPModal]);

  return maskUrlComp ? (
    <section className="overflowY-hidden overflowX-hidden h-full-page bg-color-primary flex flex-col justify-center items-center">
      {showLoader ? (
        <span className="loader"></span>
      ) : (
        <MaskedUrlErrorComponent />
      )}
    </section>
  ) : (
    <section className="home-container overflowX-hidden bg-color-primary flex flex-col justify-between items-center pt-10 pb-25">
      <HeroPageTemplate
        heading={hasGodrejPosition ? isPreWorkshopPosition ? "GPL Alchemy || Operations Academy || Concrete Formwork Masters || Pre Workshop Assessment" : isPostWorkshopPosition ? "GPL Alchemy || Operations Academy || Concrete Formwork Masters || Post Workshop Assessment" : "Operations Knowledge Assessment" : loginType === LOGIN_TYPE.THIRD_PARTY ? "Delhivery Skills Development Program: Online Entrance Exam" : t("welcome_aboard.welcome") + t("welcome_aboard.aboard")}
        content={t("login_screen.content")}
        companyLogo={companyLogo}
        hideBgBars={isMobile}
        isMobile={isMobile}
        headingSize={60}
      >
        <UserForm
          name="emailSubmitForm"
          submitForm={hasThirdPartyLogin ? onCandidateKeySubmit : onEmailSubmit}
          formInitialValues={USER_LOGIN_INITIAL_VALUES}
          form={loginForm}
        >
          <div className="form">
            {
              error ?
                <p className="error color-primary size-14 mb-10 p-10 flex items-center">
                  <span className='flex items-center mr-4'>
                    <ErrorWarning />
                  </span>
                  {
                    // error === "This assessment is not active yet, please try after designated time or contact your HR for more details." ? 
                    // "This assessment is not active as the designated time is over." :
                    error === "User is not whitelisted for this position." ?
                      t("login_screen.login_common_err") :
                      error
                  }
                </p> : <></>
            }
            <LanguageSelect
              visible={languagesOptions?.length > 0}
              className="mb-14"
              selectClassName="login-language-select"
            />
            {
              !(hasPhoneLogin) &&
              <Form.Item
                name="candidateEmail"
                rules={[
                  {
                    required: !hasPhoneLogin ? true : false,
                    message: t("login_screen.email_empty"),
                  },
                  {
                    type: 'email',
                    validateTrigger: 'onSubmit',
                    message: t("login_screen.email_wrong"),
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  autoCapitalize="off"
                  placeholder={t("login_screen.email_placeholder")}
                  className="email-input"
                  onPaste={({ target: { value } }) => { loginForm.setFieldsValue({ candidateEmail: value.trim() }) }}
                  onChange={({ target: { value } }) => { loginForm.setFieldsValue({ candidateEmail: value.trim() }) }}
                />
              </Form.Item>
            }
            {
              hasPhoneLogin &&
              <PhoneInput
                className="constraint-width"
                setValue={setCandidatePhone}
                country={country}
                setCountry={setCountry}
              />
            }
            {
              hasThirdPartyLogin &&
              <>
                <Form.Item
                  name="candidateKey"
                  rules={[
                    {
                      required: true,
                      message: t("login_screen.candidatekey_empty"),
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    autoCapitalize="off"
                    placeholder={t("login_screen.candidatekey_placeholder")}
                    className="email-input"
                    onPaste={({ target: { value } }) => { loginForm.setFieldsValue({ candidateKey: value.trim() }) }}
                    onChange={({ target: { value } }) => { loginForm.setFieldsValue({ candidateKey: value.trim() }) }}
                  />
                </Form.Item>
              </>
            }
            <Form.Item className={isMobile ? 'mt-20' : ''}>
              <Button loading={submitting} className="submit-btn" type="primary" htmlType="submit">
                {isMobile ? t("webcam.continue") : t("submit")}
              </Button>
            </Form.Item>
          </div>
        </UserForm>
      </HeroPageTemplate>
      {
        isMobile ? (
          <UnberryModal
            bottomAligned={true}
            visible={showOTPModal}
            footer={false}
            width={'430px'}
            onCancel={() => setShowOTPModal(false)}
            bodyStyle={{
              height: '410px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '50px 60px',
              mixBlendMode: 'normal',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
            }}
            maskStyle={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
            wrapClassName="otp-model otp-model-mobile"
            maskClosable={false}
          >
            <VerifyOtp
              onResendOtp={onEmailSubmit}
              countryCode={country.phone}
              candidatePhone={candidatePhone}
              candidateEmail={candidateEmail}
              hasPhoneLogin={hasPhoneLogin}
              otpLoginSuccess={otpLoginSuccess}
              onSubmit={onOTPSubmit}
              loginError={error}
            />
          </UnberryModal>
        ) : (
          <UnberryModal
            visible={showOTPModal}
            footer={false}
            width={'430px'}
            closeIcon={<Cross />}
            onCancel={() => setShowOTPModal(false)}
            bodyStyle={{
              height: 'fit-content',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '50px 60px',
              mixBlendMode: 'normal',
              borderRadius: '20px',
            }}
            maskStyle={{ backgroundColor: 'rgba(0,0,0,0.95)' }}
            wrapClassName="otp-model"
            maskClosable={false}
          >
            <VerifyOtp
              onResendOtp={onEmailSubmit}
              countryCode={country.phone}
              candidatePhone={candidatePhone}
              candidateEmail={candidateEmail}
              hasPhoneLogin={hasPhoneLogin}
              otpLoginSuccess={otpLoginSuccess}
              onSubmit={onOTPSubmit}
              loginError={error}
            />
          </UnberryModal>
        )
      }

      {/* Update User profile */}
      <UnberryModal
        visible={showUserDetailsModal}
        footer={false}
        closeIcon={<></>}
        keyboard={false}
        // closeIcon={<img src="/assets/icons/cross.svg" alt="cross" onClick={() => setShowUserDetailsModal(false)} />}
        bodyStyle={LoadingScreenUtils.modalBodyStyles}
        maskStyle={LoadingScreenUtils.modalMaskStyles}
      >
        {/* <p className="color-secondary font-medium size-14 mb-10 opacity-70">Step 2/2</p> */}
        <p
          className="color-primary font-bold size-30 mb-30"
          style={{ letterSpacing: '-1.5808px' }}
        >
          Let's hear about you
        </p>
        {/* <p className="color-primary font-medium opacity-70 mb-50" style={{ letterSpacing: '-0.225px' }}>
          Want to work in a company that loves design? sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. </p> */}
        <div className="width-p-100">
          <UserForm
            name="userDetailsForm"
            className="user-details-form"
            submitForm={onUserDetailsSubmit}
            formInitialValues={USER_PROFILE_INITIAL_VALUES}
            form={userDetailForm}
          >
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Enter your full name',
                },
              ]}
            >
              <Input placeholder="Full Name" />
            </Form.Item>
            <Form.Item
              name="designation"
              rules={[
                {
                  required: true,
                  message: 'Designation is required',
                },
              ]}
              className="mb-25"
            >
              <Input placeholder="Enter Designation" />
            </Form.Item>

            {/* <Form.Item
              name="dob"
              rules={[
                {
                  required: true,
                  message: 'D.O.B is required'
                }
              ]}
            >
              <DatePicker format="DD-MM-YYYY" placeholder="Date of Birth" />
            </Form.Item> */}

            <Form.Item noStyle>
              <Button
                loading={apiInProgress}
                style={{ marginTop: '40px' }}
                htmlType="submit"
                type="primary"
                block
              >
                Save &amp; Continue
              </Button>
            </Form.Item>
          </UserForm>
        </div>
      </UnberryModal>
      <PermissionsModal
        visible={!!(isPermissionModalVisible)}
        type={isPermissionModalVisible}
        device={PERMISSION_DEVICE_TYPES.CAMERA_MIC}
      />
    </section >
  );
};

export default LoadingScreen;
